import React, { useMemo, useRef, useState } from 'react'
import { CircleHelp } from 'lucide-react'

import '../../design/styles/help.css'

import { getHelpByScreenId } from '../../help/help-utils.js'
import { printContextualHelp } from '../../help/print-contextual-help.js'
import Modal from '../../components/modal'
import HelpDetailContent from './HelpDetailContent'

/**
 * Botón de ayuda contextual: abre la documentación de una pantalla/sub-pantalla.
 * @param {{ screenId: string, Alert?: Function, className?: string, label?: string, iconOnly?: boolean }} props
 */
const ScreenHelpButton = ({ screenId, Alert, className = '', label = 'Ayuda', iconOnly = false }) => {
  const [open, setOpen] = useState(false)
  const contentRef = useRef(null)

  const help = useMemo(() => getHelpByScreenId(screenId), [screenId])

  if (!help) return null

  const modalTitle = `Ayuda — ${help.label}`

  const handlePrint = () => {
    if (!contentRef.current) return
    const ok = printContextualHelp({
      title: modalTitle,
      contentHtml: contentRef.current.innerHTML,
    })
    if (!ok && Alert) {
      Alert({ msg: 'No se pudo abrir la ventana de impresión.', type: 'warning' })
    }
  }

  return (
    <>
      <button
        type="button"
        className={`screen-help-btn${iconOnly ? ' screen-help-btn--icon-only' : ''} ${className}`.trim()}
        onClick={() => setOpen(true)}
        title={label}
        aria-label={label}
      >
        <CircleHelp size={iconOnly ? 18 : 22} strokeWidth={2} aria-hidden />
        {!iconOnly && <span className="screen-help-btn__label">{label}</span>}
      </button>

      {open && (
        <Modal
          title={modalTitle}
          modalClose={() => setOpen(false)}
          modalId={`screen-help-${screenId}`}
          Alert={Alert}
          width="min(720px, 95vw)"
        >
          <div className="screen-help-panel">
            <div className="screen-help-panel__content" ref={contentRef}>
              {help.navPath && (
                <p className="screen-help-panel__nav">{help.navPath}</p>
              )}
              {help.summary && (
                <p className="screen-help-panel__summary">{help.summary}</p>
              )}
              {help.detail?.sections?.length > 0 ? (
                <HelpDetailContent detail={help.detail} />
              ) : (
                <p className="screen-help-panel__pending">
                  La ayuda de esta sección estará disponible pronto.
                </p>
              )}
            </div>
            <div className="screen-help-panel__actions">
              <button type="button" className="btn-small" onClick={handlePrint}>
                Imprimir
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

export default ScreenHelpButton
